import React, {
    useEffect,
    useState
} from "react";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";

import QuillEditor from "../components/QuillEditor";


function EditCategory() {

    const {
        id
    } = useParams();

    const navigate = useNavigate();


    // =========================================================
    // FORM DATA
    // =========================================================

    const [formData, setFormData] = useState({

        paper: "",

        price: "",

        duration: "",

        link: "",

        detail1: "",

        detail2: "",

        detail3: "",

        detail4: "",

        negative_marks: "0.00",

        marks: "",

        show_on_home: "No",

        status: "Enable"

    });


    // =========================================================
    // LOADING
    // =========================================================

    const [loading, setLoading] = useState(true);


    // =========================================================
    // SAVING
    // =========================================================

    const [saving, setSaving] = useState(false);


    // =========================================================
    // GET PAPER CATEGORY
    // =========================================================

    useEffect(() => {

        const fetchCategory = async () => {

            try {

                setLoading(true);


                const response = await fetch(

                    `${API_BASE_URL}/admin/paper-category/${id}`,

                    {
                        method: "GET",

                        headers: {
                            "Accept":
                                "application/json"
                        },

                        credentials: "include"
                    }

                );


                const responseText =
                    await response.text();


                let data = {};

                try {

                    data = responseText
                        ? JSON.parse(responseText)
                        : {};

                } catch (jsonError) {

                    console.error(
                        "Invalid JSON Response:",
                        responseText
                    );

                }


                console.log(
                    "Paper Category Response:",
                    data
                );


                // =====================================================
                // UNAUTHORIZED
                // =====================================================

                if (
                    response.status === 401 ||
                    response.status === 403
                ) {

                    await Swal.fire({

                        icon: "warning",

                        title: "Unauthorized",

                        text:
                            data.message ||
                            "You are not authorized."

                    });


                    navigate(
                        "/login",
                        {
                            replace: true
                        }
                    );


                    return;
                }


                // =====================================================
                // SERVER ERROR
                // =====================================================

                if (!response.ok) {

                    throw new Error(

                        data?.error ||

                        data?.message ||

                        `Server Error (${response.status})`

                    );

                }


                // =====================================================
                // SUCCESS
                // =====================================================

                if (
                    data.success
                ) {

                    const category =
                        data.data ||
                        data.category;


                    if (!category) {

                        throw new Error(
                            "Paper category data not found."
                        );

                    }


                    setFormData({

                        paper:
                            category.paper ??
                            "",

                        price:
                            category.price ??
                            "",

                        duration:
                            category.duration ??
                            "",

                        link:
                            category.link ??
                            "",

                        detail1:
                            category.detail1 ??
                            "",

                        detail2:
                            category.detail2 ??
                            "",

                        detail3:
                            category.detail3 ??
                            "",

                        detail4:
                            category.detail4 ??
                            "",

                        negative_marks:
                            category.negative_marks ??
                            "0.00",

                        marks:
                            category.marks ??
                            "",

                        show_on_home:
                            category.show_on_home ??
                            "No",

                        status:
                            category.status ??
                            "Enable"

                    });


                } else {

                    await Swal.fire({

                        icon: "error",

                        title: "Category Not Found",

                        text:
                            data.message ||
                            "Paper category not found."

                    });


                    navigate(
                        "/paper-category"
                    );

                }


            } catch (error) {

                console.error(
                    "Fetch Category Error:",
                    error
                );


                Swal.fire({

                    icon: "error",

                    title: "Server Error",

                    text:
                        error.message ||
                        "Unable to connect with server."

                });

            } finally {

                setLoading(false);

            }

        };


        if (id) {

            fetchCategory();

        }

    }, [
        id,
        navigate
    ]);


    // =========================================================
    // HANDLE NORMAL INPUT
    // =========================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData(
            previous => ({

                ...previous,

                [name]: value

            })
        );

    };


    // =========================================================
    // HANDLE QUILL
    // =========================================================

    const handleQuillChange = (
        field,
        value
    ) => {

        setFormData(
            previous => ({

                ...previous,

                [field]:
                    value || ""

            })
        );

    };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        if (saving) {

            return;

        }


        // =====================================================
        // VALIDATION
        // =====================================================

        if (
            !formData.paper.trim()
        ) {

            Swal.fire({

                icon: "warning",

                title: "Paper Required",

                text:
                    "Please enter paper title."

            });

            return;

        }


        if (
            formData.price === "" ||
            Number(formData.price) < 0
        ) {

            Swal.fire({

                icon: "warning",

                title: "Invalid Price",

                text:
                    "Please enter a valid price."

            });

            return;

        }


        if (
            !formData.duration.trim()
        ) {

            Swal.fire({

                icon: "warning",

                title: "Duration Required",

                text:
                    "Please enter duration."

            });

            return;

        }


        if (
            formData.negative_marks === "" ||
            Number(formData.negative_marks) < 0
        ) {

            Swal.fire({

                icon: "warning",

                title: "Invalid Negative Marks",

                text:
                    "Please enter valid negative marks."

            });

            return;

        }


        if (
            !String(
                formData.marks
            ).trim()
        ) {

            Swal.fire({

                icon: "warning",

                title: "Marks Required",

                text:
                    "Please enter marks for every question."

            });

            return;

        }


        // =====================================================
        // UPDATE
        // =====================================================

        try {

            setSaving(true);


            const payload = {

                paper:
                    formData.paper.trim(),

                price:
                    Number(
                        formData.price
                    ),

                duration:
                    formData.duration.trim(),

                link:
                    formData.link
                        ? formData.link.trim()
                        : "",

                /*
                |-----------------------------------------------------
                | IMPORTANT
                |-----------------------------------------------------
                | Quill HTML ko trim() nahi kar rahe.
                | Isme emoji + HTML formatting ho sakti hai.
                */

                detail1:
                    formData.detail1 || "",

                detail2:
                    formData.detail2 || "",

                detail3:
                    formData.detail3 || "",

                detail4:
                    formData.detail4 || "",

                negative_marks:
                    Number(
                        formData.negative_marks
                    ).toFixed(2),

                marks:
                    String(
                        formData.marks
                    ).trim(),

                show_on_home:
                    formData.show_on_home,

                status:
                    formData.status

            };


            console.log(
                "Update Category Payload:",
                payload
            );


            const response = await fetch(

                `${API_BASE_URL}/admin/paper-category/${id}`,

                {

                    method: "PUT",

                    headers: {

                        "Accept":
                            "application/json",

                        "Content-Type":
                            "application/json"

                    },

                    credentials: "include",

                    body:
                        JSON.stringify(
                            payload
                        )

                }

            );


            // =====================================================
            // READ RESPONSE
            // =====================================================

            const responseText =
                await response.text();


            let data = {};

            try {

                data =
                    responseText
                        ? JSON.parse(
                            responseText
                        )
                        : {};

            } catch (jsonError) {

                console.error(
                    "Invalid JSON Response:",
                    responseText
                );

            }


            console.log(
                "Update Category Response:",
                data
            );


            // =====================================================
            // VALIDATION ERROR
            // =====================================================

            if (
                response.status === 422
            ) {

                let errorMessage =
                    data?.message ||
                    "Please check the form fields.";


                if (
                    data?.errors
                ) {

                    const errors =
                        Object
                            .values(
                                data.errors
                            )
                            .flat()
                            .join("\n");


                    if (errors) {

                        errorMessage =
                            errors;

                    }

                }


                Swal.fire({

                    icon: "error",

                    title: "Validation Error",

                    text:
                        errorMessage

                });


                return;

            }


            // =====================================================
            // UNAUTHORIZED
            // =====================================================

            if (
                response.status === 401 ||
                response.status === 403
            ) {

                Swal.fire({

                    icon: "warning",

                    title: "Unauthorized",

                    text:
                        data?.message ||
                        "You are not authorized to update this category."

                });


                return;

            }


            // =====================================================
            // SERVER ERROR
            // =====================================================

            if (!response.ok) {

                const errorMessage =

                    data?.error ||

                    data?.message ||

                    responseText ||

                    `Server Error (${response.status})`;


                console.error(
                    "Update Category Server Error:",
                    {
                        status:
                            response.status,

                        data,

                        responseText
                    }
                );


                Swal.fire({

                    icon: "error",

                    title: "Server Error",

                    text:
                        errorMessage

                });


                return;

            }


            // =====================================================
            // SUCCESS
            // =====================================================

            if (
                response.ok &&
                data.success
            ) {

                await Swal.fire({

                    icon: "success",

                    title: "Updated Successfully",

                    text:
                        data.message ||
                        "Paper category updated successfully.",

                    confirmButtonText:
                        "OK",

                    confirmButtonColor:
                        "#198754"

                });


                navigate(
                    "/paper-category"
                );


                return;

            }


            // =====================================================
            // OTHER ERROR
            // =====================================================

            Swal.fire({

                icon: "error",

                title: "Update Failed",

                text:
                    data?.message ||
                    "Unable to update paper category."

            });


        } catch (error) {

            console.error(
                "Update Category Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    error?.message ||
                    "Unable to connect with server."

            });

        } finally {

            setSaving(false);

        }

    };


    // =========================================================
    // LOADING SCREEN
    // =========================================================

    if (loading) {

        return (

            <div
                className="container-fluid"
            >

                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                        minHeight: "500px"
                    }}
                >

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    >

                        <span
                            className="visually-hidden"
                        >
                            Loading...
                        </span>

                    </div>

                </div>

            </div>

        );

    }


    // =========================================================
    // JSX
    // =========================================================

    return (

        <div
            className="container-fluid"
            style={{
                maxHeight:
                    "85vh",

                overflowY:
                    "auto"
            }}
        >

            <div
                className="row justify-content-center"
            >

                <div
                    className="col-xl-9 col-lg-10 col-md-12"
                >

                    <div
                        className="card shadow-sm border-0"
                    >

                        {/* =================================================
                            HEADER
                        ================================================= */}

                        <div
                            className="card-header bg-primary text-white py-3"
                        >

                            <div
                                className="d-flex justify-content-between align-items-center"
                            >

                                <div>

                                    <h4
                                        className="mb-1"
                                    >

                                        <i className="bi bi-pencil-square me-2"></i>

                                        Edit Paper Plan

                                    </h4>


                                    <small>
                                        Update paper plan details
                                    </small>

                                </div>


                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={() =>
                                        navigate(
                                            "/paper-category"
                                        )
                                    }
                                    disabled={saving}
                                >

                                    <i className="bi bi-arrow-left me-1"></i>

                                    Back

                                </button>

                            </div>

                        </div>


                        {/* =================================================
                            BODY
                        ================================================= */}

                        <div
                            className="card-body p-4"
                        >

                            <form
                                onSubmit={
                                    handleSubmit
                                }
                            >

                                {/* =================================================
                                    BASIC INFORMATION
                                ================================================= */}

                                <h5
                                    className="border-bottom pb-2 mb-4"
                                >

                                    <i className="bi bi-info-circle me-2"></i>

                                    Basic Information

                                </h5>


                                {/* PAPER */}

                                <div
                                    className="mb-4"
                                >

                                    <label
                                        className="form-label fw-semibold"
                                    >

                                        Paper Title

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        name="paper"
                                        className="form-control"
                                        placeholder="Enter paper title"
                                        value={
                                            formData.paper
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength="250"
                                        disabled={saving}
                                    />

                                </div>


                                {/* PRICE + DURATION */}

                                <div
                                    className="row"
                                >

                                    <div
                                        className="col-md-6 mb-4"
                                    >

                                        <label
                                            className="form-label fw-semibold"
                                        >

                                            Price (₹)

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <input
                                            type="number"
                                            name="price"
                                            className="form-control"
                                            placeholder="Enter price"
                                            min="0"
                                            step="1"
                                            value={
                                                formData.price
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            disabled={saving}
                                        />

                                    </div>


                                    <div
                                        className="col-md-6 mb-4"
                                    >

                                        <label
                                            className="form-label fw-semibold"
                                        >

                                            Duration

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <input
                                            type="text"
                                            name="duration"
                                            className="form-control"
                                            placeholder="Example: 30 Minutes"
                                            value={
                                                formData.duration
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            maxLength="100"
                                            disabled={saving}
                                        />

                                    </div>

                                </div>


                                {/* LINK */}

                                <div
                                    className="mb-4"
                                >

                                    <label
                                        className="form-label fw-semibold"
                                    >

                                        Purchase Link

                                    </label>


                                    <input
                                        type="text"
                                        name="link"
                                        className="form-control"
                                        placeholder="https://example.com/payment"
                                        value={
                                            formData.link
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength="100"
                                        disabled={saving}
                                    />

                                </div>


                                {/* =================================================
                                    PLAN DETAILS
                                ================================================= */}

                                <h5
                                    className="border-bottom pb-2 mb-4 mt-4"
                                >

                                    <i className="bi bi-list-check me-2"></i>

                                    Plan Details

                                </h5>


                                {/* DETAIL 1 */}

                                <div
                                    className="mb-4"
                                >

                                    <label
                                        className="form-label fw-semibold"
                                    >

                                        Detail 1

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.detail1
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            handleQuillChange(
                                                "detail1",
                                                value
                                            )
                                        }
                                    />

                                </div>


                                {/* DETAIL 2 */}

                                <div
                                    className="mb-4"
                                >

                                    <label
                                        className="form-label fw-semibold"
                                    >

                                        Detail 2

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.detail2
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            handleQuillChange(
                                                "detail2",
                                                value
                                            )
                                        }
                                    />

                                </div>


                                {/* DETAIL 3 */}

                                <div
                                    className="mb-4"
                                >

                                    <label
                                        className="form-label fw-semibold"
                                    >

                                        Detail 3

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.detail3
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            handleQuillChange(
                                                "detail3",
                                                value
                                            )
                                        }
                                    />

                                </div>


                                {/* DETAIL 4 */}

                                <div
                                    className="mb-4"
                                >

                                    <label
                                        className="form-label fw-semibold"
                                    >

                                        Detail 4

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.detail4
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            handleQuillChange(
                                                "detail4",
                                                value
                                            )
                                        }
                                    />

                                </div>


                                {/* =================================================
                                    MARKING SYSTEM
                                ================================================= */}

                                <h5
                                    className="border-bottom pb-2 mb-4 mt-4"
                                >

                                    <i className="bi bi-calculator me-2"></i>

                                    Marking System

                                </h5>


                                <div
                                    className="row"
                                >

                                    {/* NEGATIVE MARKS */}

                                    <div
                                        className="col-md-6 mb-4"
                                    >

                                        <label
                                            className="form-label fw-semibold"
                                        >

                                            Negative Marks

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <input
                                            type="number"
                                            name="negative_marks"
                                            className="form-control"
                                            placeholder="Example: 0.25"
                                            min="0"
                                            step="0.01"
                                            value={
                                                formData.negative_marks
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            disabled={saving}
                                        />


                                        <small
                                            className="text-muted"
                                        >

                                            Negative marks for an incorrect answer.

                                        </small>

                                    </div>


                                    {/* MARKS */}

                                    <div
                                        className="col-md-6 mb-4"
                                    >

                                        <label
                                            className="form-label fw-semibold"
                                        >

                                            Marks Per Question

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <input
                                            type="text"
                                            name="marks"
                                            className="form-control"
                                            placeholder="Example: 1"
                                            value={
                                                formData.marks
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            maxLength="20"
                                            disabled={saving}
                                        />


                                        <small
                                            className="text-muted"
                                        >

                                            Marks awarded for every correct answer.

                                        </small>

                                    </div>

                                </div>


                                {/* =================================================
                                    SETTINGS
                                ================================================= */}

                                <h5
                                    className="border-bottom pb-2 mb-4 mt-2"
                                >

                                    <i className="bi bi-gear me-2"></i>

                                    Settings

                                </h5>


                                <div
                                    className="row"
                                >

                                    {/* SHOW ON HOME */}

                                    <div
                                        className="col-md-6 mb-4"
                                    >

                                        <label
                                            className="form-label fw-semibold"
                                        >

                                            Show On Home

                                        </label>


                                        <select
                                            name="show_on_home"
                                            className="form-select"
                                            value={
                                                formData.show_on_home
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            disabled={saving}
                                        >

                                            <option value="No">
                                                No
                                            </option>

                                            <option value="Yes">
                                                Yes
                                            </option>

                                        </select>

                                    </div>


                                    {/* STATUS */}

                                    <div
                                        className="col-md-6 mb-4"
                                    >

                                        <label
                                            className="form-label fw-semibold"
                                        >

                                            Status

                                        </label>


                                        <select
                                            name="status"
                                            className="form-select"
                                            value={
                                                formData.status
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            disabled={saving}
                                        >

                                            <option value="Enable">
                                                Enable
                                            </option>

                                            <option value="Disable">
                                                Disable
                                            </option>

                                        </select>

                                    </div>

                                </div>


                                {/* =================================================
                                    BUTTONS
                                ================================================= */}

                                <div
                                    className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top"
                                >

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            navigate(
                                                "/paper-category"
                                            )
                                        }
                                        disabled={saving}
                                    >

                                        <i className="bi bi-x-circle me-1"></i>

                                        Cancel

                                    </button>


                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={saving}
                                    >

                                        {saving ? (

                                            <>

                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                />

                                                Updating...

                                            </>

                                        ) : (

                                            <>

                                                <i className="bi bi-check-circle me-1"></i>

                                                Update Paper Plan

                                            </>

                                        )}

                                    </button>

                                </div>

                            </form>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}


export default EditCategory;
